/**
 * DynamicFieldDisplay.tsx — Renders a single form field value
 * driven by the DynamicField schema metadata.
 *
 * Supports: boolean, badges, arrays, currency, number, date, status badge, text.
 */

import React from 'react';
import type { DynamicEntityResolvedField } from '@/modules/programs/types/dynamicEntityView';

interface DynamicFieldDisplayProps {
  field: DynamicEntityResolvedField;
  className?: string;
}

// ─── Status badge color map ───────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-success/10 text-success border border-success/20',
  inactive: 'bg-muted text-muted-foreground border border-border',
  suspended: 'bg-warning/10 text-warning border border-warning/20',
  draft: 'bg-muted text-muted-foreground border border-border',
  published: 'bg-info/10 text-info border border-info/20',
  archived: 'bg-muted text-muted-foreground border border-border',
  rejected: 'bg-destructive/10 text-destructive border border-destructive/20',
  pending: 'bg-warning/10 text-warning border border-warning/20',
  completed: 'bg-success/10 text-success border border-success/20',
};

function getStatusBadgeClass(status: string): string {
  return STATUS_COLORS[status.toLowerCase()] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
}

// ─── Formatting helpers ───────────────────────────────────────

function formatCurrency(value: unknown): string {
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(num)) return String(value);
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatNumber(value: unknown): string {
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(num)) return String(value);
  return new Intl.NumberFormat('en-AU', {
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(value: unknown): string {
  if (!value) return '';
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(value: unknown): string {
  if (!value) return '';
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Sentence case for badge text ─────────────────────────────

function sentenceCase(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// ─── Main render ──────────────────────────────────────────────

function renderValue(field: DynamicEntityResolvedField): React.ReactNode {
  if (field.value === null || field.value === undefined || field.displayValue === '') {
    return <span className="text-muted-foreground italic">—</span>;
  }

  // Boolean
  if (field.type === 'boolean') {
    return (
      <span className={field.value ? 'text-success' : 'text-muted-foreground'}>
        {field.value ? 'Yes' : 'No'}
      </span>
    );
  }

  // Status badge (via semanticRole or field type)
  if (field.semanticRole === 'STATUS' || field.type === 'status') {
    const status = String(field.displayValue || field.value);
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${getStatusBadgeClass(status)}`}
      >
        {sentenceCase(status)}
      </span>
    );
  }

  // Currency
  if (field.type === 'currency') {
    return <span className="text-[12px] font-medium tabular-nums">{formatCurrency(field.value)}</span>;
  }

  // Number / decimal
  if (field.type === 'number' || field.type === 'decimal') {
    return <span className="text-[12px] tabular-nums">{formatNumber(field.value)}</span>;
  }

  // Date
  if (field.type === 'date') {
    return <span className="text-[12px]">{formatDate(field.value)}</span>;
  }

  // DateTime
  if (field.type === 'datetime') {
    return <span className="text-[12px]">{formatDateTime(field.value)}</span>;
  }

  // Explicit badges
  if (field.badges && field.badges.length > 0) {
    return (
      <div className="flex flex-wrap gap-1">
        {field.badges.map((badge, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
          >
            {sentenceCase(badge)}
          </span>
        ))}
      </div>
    );
  }

  // Array values
  if (Array.isArray(field.value)) {
    return (
      <span className="text-[12px]">{(field.value as string[]).join(', ')}</span>
    );
  }

  // Default text
  return <span className="text-[12px]">{field.displayValue}</span>;
}

export const DynamicFieldDisplay: React.FC<DynamicFieldDisplayProps> = ({
  field,
  className = '',
}) => {
  if (!field.visible) return null;

  return (
    <div className={`flex flex-col gap-0.5 ${className}`}>
      <dt className="text-[12px] font-medium text-muted-foreground truncate">
        {field.label}
      </dt>
      <dd className="text-[12px] text-foreground">{renderValue(field)}</dd>
    </div>
  );
};
